import React, { useMemo, useState } from 'react';
import PreviewPopout from '../Controls/PreviewPopout';
import useSharedRemoteRuntime from '../Remote/useSharedRemoteRuntime';
import { requestRemoteState } from '../Controls/remoteStateApi';

const previewTransport = { requestState: requestRemoteState };

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return 'Unavailable';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const scaled = bytes / (1024 ** exponent);
  const precision = scaled >= 100 || exponent === 0 ? 0 : 1;
  return `${scaled.toFixed(precision)} ${units[exponent]}`;
};

const formatPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'Unavailable';
  return `${numeric.toFixed(1)}%`;
};

const formatTimestamp = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 'Unavailable';
  const date = new Date(numeric);
  return Number.isNaN(date.getTime()) ? 'Unavailable' : date.toLocaleString();
};

const formatAge = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 'Unavailable';
  const deltaMs = Date.now() - numeric;
  if (deltaMs < 0) return 'Just now';
  const seconds = Math.floor(deltaMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const STATUS_LABELS = {
  launching: 'Launching',
  launching_unverified: 'Launching (unverified)',
  running: 'Running',
  returned_to_suitestream: 'Returned to Suitestream',
  launch_unconfirmed: 'Launch unconfirmed',
  monitoring_unavailable: 'Monitoring unavailable',
  suitestream: 'Suitestream active',
  unknown: 'Unknown',
};

function ExternalAppMonitor({ device, externalApp }) {
  const [previewWindowVisible, setPreviewWindowVisible] = useState(false);
  const {
    canPreviewSnapshot,
    fetchLiveSnapshot,
    liveSnapshot,
    liveSnapshotLabel,
    protectedVideoLikely,
    snapshotMessage,
    snapshotStatus,
    snapshotUnsupportedMessage,
  } = useSharedRemoteRuntime({
    device,
    transport: previewTransport,
    previewBackgroundIntervalOverride: 15000,
    previewWindowVisible,
  });
  const refreshing = snapshotStatus === 'loading' || snapshotStatus === 'refreshing';

  const appName =
    externalApp?.foregroundAppLabel ||
    externalApp?.targetAppLabel ||
    externalApp?.foregroundPackageName ||
    externalApp?.targetPackageName ||
    'Unknown app';

  const health = externalApp?.deviceHealth || {};
  const memorySummary = useMemo(() => {
    const used = formatBytes(health.memoryUsedBytes);
    const total = formatBytes(health.memoryTotalBytes);
    return used === 'Unavailable' || total === 'Unavailable' ? 'Unavailable' : `${used} / ${total}`;
  }, [health.memoryUsedBytes, health.memoryTotalBytes]);

  const storageSummary = useMemo(() => {
    const free = formatBytes(health.storageAvailableBytes);
    const total = formatBytes(health.storageTotalBytes);
    return free === 'Unavailable' || total === 'Unavailable' ? 'Unavailable' : `${free} free / ${total}`;
  }, [health.storageAvailableBytes, health.storageTotalBytes]);

  return (
    <div className="external-app-monitor">
      <div className="external-app-monitor__hero">
        <div>
          <p className="external-app-monitor__eyebrow">External App Monitor</p>
          <h3>{appName}</h3>
          <p className="external-app-monitor__subtle">
            {STATUS_LABELS[externalApp?.status] || STATUS_LABELS.unknown}
            {externalApp?.isStale ? ' · Telemetry stale' : ''}
          </p>
        </div>
        <div className={`external-app-monitor__status external-app-monitor__status--${externalApp?.isStale ? 'stale' : 'live'}`}>
          {externalApp?.isStale ? 'Stale' : 'Live'}
        </div>
      </div>

      <div className="external-app-monitor__grid">
        <section className="external-app-monitor__panel dashboard-tv-remote-preview">
          <div className="dashboard-tv-remote-panel-header">
            <h4>Live Preview</h4>
            <button
              type="button"
              className="secondary"
              onClick={() => fetchLiveSnapshot('manual')}
              disabled={refreshing || !canPreviewSnapshot || Boolean(snapshotUnsupportedMessage)}
            >
              {refreshing ? 'Refreshing…' : 'Refresh Now'}
            </button>
            <PreviewPopout key={device?.id} name={device?.name || 'Unknown device'}
              image={liveSnapshot?.screenshot?.dataUrl} caption={snapshotMessage || liveSnapshotLabel}
              onVisibilityChange={setPreviewWindowVisible} onRefresh={() => fetchLiveSnapshot('manual')}
              refreshDisabled={refreshing || !canPreviewSnapshot || Boolean(snapshotUnsupportedMessage)} />
          </div>
          {liveSnapshot?.screenshot?.dataUrl ? (
            <div className="dashboard-tv-remote-preview-frame">
              <img
                src={liveSnapshot.screenshot.dataUrl}
                alt={`Live preview for ${device?.name || 'Unknown device'}`}
                className="dashboard-tv-remote-preview-image"
              />
            </div>
          ) : (
            <div className="dashboard-tv-remote-preview-empty">
              {snapshotUnsupportedMessage || (!canPreviewSnapshot
                ? 'Live preview requires the Suitestream app/accessibility connection.'
                : snapshotStatus === 'error'
                  ? 'Live preview is unavailable right now.'
                  : 'Waiting for the first screenshot.')}
            </div>
          )}
          <div className="dashboard-tv-remote-hint">Refreshes every 15 seconds while this page is visible.</div>
          <div className="dashboard-tv-remote-hint">{liveSnapshotLabel}</div>
          {snapshotMessage && <div className="dashboard-tv-remote-hint" role="status">{snapshotMessage}</div>}
          {protectedVideoLikely && (
            <div className="dashboard-tv-remote-preview-warning">
              Preview has been black for several captures. Protected video may be playing and unavailable due to copyright restrictions.
            </div>
          )}
        </section>

        <section className="external-app-monitor__panel">
          <h4>App &amp; Status</h4>
          <div className="external-app-monitor__stat">
            <span>Foreground</span>
            <strong>{externalApp?.foregroundAppLabel || 'Unavailable'}</strong>
          </div>
          <div className="external-app-monitor__meta">
            <div>
              <span>Package</span>
              <strong>{externalApp?.foregroundPackageName || 'Unavailable'}</strong>
            </div>
            <div>
              <span>Class</span>
              <strong>{externalApp?.foregroundClassName || 'Unavailable'}</strong>
            </div>
          </div>
          <div className="external-app-monitor__meta">
            <div>
              <span>Expected App</span>
              <strong>{externalApp?.targetAppLabel || 'Unavailable'}</strong>
            </div>
            <div>
              <span>Expected Package</span>
              <strong>{externalApp?.targetPackageName || 'Unavailable'}</strong>
            </div>
          </div>
          <div className="external-app-monitor__stat">
            <span>Playback Status</span>
            <strong>{STATUS_LABELS[externalApp?.status] || STATUS_LABELS.unknown}</strong>
          </div>
          <div className="external-app-monitor__meta">
            <div>
              <span>Last Telemetry</span>
              <strong>{formatAge(externalApp?.lastTelemetryAt || externalApp?.collectedAt)}</strong>
            </div>
            <div>
              <span>Telemetry Time</span>
              <strong>{formatTimestamp(externalApp?.collectedAt)}</strong>
            </div>
          </div>
          <div className="external-app-monitor__meta">
            <div>
              <span>Launch Requested</span>
              <strong>{formatTimestamp(externalApp?.lastExternalLaunchRequestAt)}</strong>
            </div>
            <div>
              <span>App Last Seen</span>
              <strong>{formatTimestamp(externalApp?.lastExternalAppSeenAt)}</strong>
            </div>
          </div>
        </section>

        <section className="external-app-monitor__panel">
          <h4>Device Health</h4>
          <div className="external-app-monitor__health">
            <div className="external-app-monitor__health-card">
              <span>CPU</span>
              <strong>{formatPercent(health.cpuUsagePercent)}</strong>
            </div>
            <div className="external-app-monitor__health-card">
              <span>Memory</span>
              <strong>{memorySummary}</strong>
            </div>
            <div className="external-app-monitor__health-card">
              <span>Storage</span>
              <strong>{storageSummary}</strong>
            </div>
          </div>
        </section>

        <section className="external-app-monitor__panel">
          <h4>Diagnostics</h4>
          <div className="external-app-monitor__meta">
            <div>
              <span>Accessibility</span>
              <strong>{externalApp?.accessibilityEnabled ? 'Enabled' : 'Disabled'}</strong>
            </div>
            <div>
              <span>Socket</span>
              <strong>{externalApp?.socketConnected ? 'Connected' : 'Disconnected'}</strong>
            </div>
          </div>
          <div className="external-app-monitor__meta">
            <div>
              <span>State Snapshot</span>
              <strong>{externalApp?.stateAvailable ? 'Available' : 'Unavailable'}</strong>
            </div>
            <div>
              <span>Signature</span>
              <strong>{externalApp?.summarySignature || 'Unavailable'}</strong>
            </div>
          </div>
          <div className="external-app-monitor__meta">
            <div>
              <span>Focused Text</span>
              <strong>{externalApp?.focusedText || 'Unavailable'}</strong>
            </div>
            <div>
              <span>Focused Description</span>
              <strong>{externalApp?.focusedDesc || 'Unavailable'}</strong>
            </div>
          </div>
        </section>
      </div>

      <div className="external-app-monitor__footer">
        <span>Device</span>
        <strong>{device?.name || 'Unknown device'}</strong>
      </div>
    </div>
  );
}

export default ExternalAppMonitor;
